import React, { useState } from "react";
import { Modal, TextInputBase } from "@truevault/aquilo";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";

/**
 * Confirmation for removing a vendor from the org entirely — both as a data source and a data
 * recipient — with a typed "remove" gate, since the removal also strips the vendor from notices
 * and policies.
 *
 * Lives in its own module so the Data Flows disclosures step can offer the same removal without
 * importing VendorInventoryPage, which would close an import cycle (VendorInventoryPage →
 * VendorDetailsModal → DataFlowsDisclosuresPage).
 *
 * `onBack` is passed only when the confirmation replaced Vendor Settings (reached from its Remove
 * Vendor button); it adds a "Vendor Settings" back link above the title. Cancel should lead to the
 * same place, so callers wire both to the same handler.
 */
export function RemoveVendorConfirmationModal({
  vendorName,
  onConfirm,
  onCancel,
  onBack,
}: {
  vendorName: string;
  onConfirm: () => void;
  onCancel: () => void;
  onBack?: () => void;
}) {
  const [removeVendorConfirmation, setRemoveVendorConfirmation] = useState("");
  return (
    <Modal
      title={
        <>
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="mb-[12px] flex items-center gap-[8px] border-none bg-transparent p-0 text-[14px] font-medium text-neutral-900 cursor-pointer"
            >
              <ArrowLeftIcon className="h-[16px] w-[16px]" />
              Vendor Settings
            </button>
          )}
          {`Are you sure you want to remove ${vendorName} from your vendor list?`}
        </>
      }
      body={`This will completely remove ${vendorName} from your organization as both a data source and a data recipient. Only do so if you are not using this vendor anymore. All information about this vendor will be removed from your notices, policies, and related documents.`}
      alignText="left"
      alignButton="right"
      secondButton="Remove Vendor"
      secondButtonColor="warning"
      onSecondButtonClick={onConfirm}
      secondButtonDisabled={removeVendorConfirmation.trim().toLowerCase() !== "remove"}
      firstButton="Cancel"
      onFirstButtonClick={onCancel}
      showOverlay
      onClose={onCancel}
    >
      <div className="text-neutral-700 text-[14px] font-normal">
        Type "remove" below to remove the vendor.
      </div>
      <TextInputBase
        field="removeVendorConfirmation"
        placeholder="Type here"
        value={removeVendorConfirmation}
        onChange={(e) => setRemoveVendorConfirmation(e.target.value)}
      />
    </Modal>
  );
}
